/**
 * CartContext — persistent shopping cart for OPIKJEWELS.
 *
 * Shape of an item:
 *   {
 *     key:             unique id "<gemId>:<variationId|''>"   ← React keys + dedupe
 *     gemstone_id:     uuid
 *     variation_id:    uuid | null   (null = base product, no variation)
 *     name:            display name
 *     variation_label: e.g. "5mm · White · 0.5 ct"  (empty for base)
 *     image_url:       primary image
 *     unit_price:      price in the gem's BASE currency (typically IDR)
 *     base_currency:   e.g. "IDR" — the currency the unit_price is denominated in
 *     stock:           max quantity available (null = unlimited)
 *     quantity:        user-chosen quantity
 *   }
 *
 * Storage: persisted in localStorage so the cart survives refresh / new tab.
 *
 * Concurrency: we intentionally don't validate stock on every change. The
 * server re-checks stock at the moment the buyer hits checkout, so the
 * worst case is a friendly "X is no longer available" toast instead of a
 * stale-cart silent loss.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { trackAddToCart } from "../lib/metaPixel";
import { trackTTAddToCart } from "../lib/tiktokPixel";

const Ctx = createContext(null);
const STORAGE_KEY = "opik_cart_v1";

const safeRead = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const itemKey = (gemId, variationId) => `${gemId}:${variationId || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => safeRead());

  // Persist to localStorage on every change. Wrapped in try/catch because
  // private-browsing modes throw on writes.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch { /* ignore quota / private mode errors */ }
  }, [items]);

  const addItem = useCallback((item, qty = 1) => {
    const key = itemKey(item.gemstone_id, item.variation_id);
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        const cur = next[idx];
        const newQty = cur.quantity + qty;
        const cap = cur.stock != null ? Math.min(newQty, cur.stock) : newQty;
        next[idx] = { ...cur, quantity: cap };
        return next;
      }
      const newItem = { ...item, key, quantity: Math.max(1, qty) };
      if (newItem.stock != null && newItem.quantity > newItem.stock) {
        newItem.quantity = newItem.stock;
      }
      return [...prev, newItem];
    });

    // Fire Meta Pixel + TikTok Pixel AddToCart so Commerce Manager sees
    // the events (matches the catalog by `id`). Wrapped — telemetry must
    // never break cart UX.
    try {
      const fakeGem = {
        id: item.gemstone_id,
        name: item.name,
        category: item.category,
        currency: item.base_currency || "USD",
      };
      // Best-effort USD value from the base currency price. CartContext
      // doesn't have FX rates available here so we approximate when
      // base currency isn't USD by sending price as-is for analytics
      // (Meta uses currency code to normalise its dashboards).
      const valueUsd = Number(item.unit_price_usd) || Number(item.unit_price) || 0;
      trackAddToCart(fakeGem, valueUsd * qty);
      trackTTAddToCart(fakeGem, valueUsd * qty);
    } catch { /* never block cart UX on telemetry */ }
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.key !== key) return i;
          const target = Math.max(0, Math.floor(qty));
          const cap = i.stock != null ? Math.min(target, i.stock) : target;
          return { ...i, quantity: cap };
        })
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    // Subtotal is computed per-currency. We don't aggregate across currencies
    // here because the storefront uses CurrencyContext.convert() at render
    // time to bridge each line item into the user's display currency.
    const byCurrency = {};
    items.forEach((i) => {
      const c = i.base_currency || "USD";
      byCurrency[c] = (byCurrency[c] || 0) + i.unit_price * i.quantity;
    });
    const count = items.reduce((acc, i) => acc + i.quantity, 0);
    return { byCurrency, count };
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clearCart, ...totals }),
    [items, addItem, updateQty, removeItem, clearCart, totals],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCart = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCart must be used inside <CartProvider>");
  return v;
};
